import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Menu, X, User, LogOut, Heart, MessageCircle, LayoutDashboard, Shield } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();

  const isAuth = !!user && user !== false;

  useEffect(() => {
    if (!isAuth) { setUnread(0); return; }
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/community/unread-total");
        setUnread(data?.unread || 0);
      } catch (_) { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => clearInterval(t);
  }, [isAuth, loc.pathname]);

  const handleLogout = async () => { await logout(); nav("/"); };

  const NavLink = ({ to, children, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className="text-sm uppercase tracking-[0.2em] transition-colors"
      style={{
        color: loc.pathname === to ? "var(--brand-primary)" : "var(--text-secondary)",
        letterSpacing: "0.2em",
      }}
      onMouseEnter={(e) => (e.target.style.color = "var(--text-primary)")}
      onMouseLeave={(e) => (e.target.style.color = loc.pathname === to ? "var(--brand-primary)" : "var(--text-secondary)")}
      onClick={() => setOpen(false)}
    >
      {children}
    </Link>
  );

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: "rgba(8,7,8,0.7)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <span className="pk2-logo-glow">
            <img src="/logo.png" alt="PK2 Scarlett" style={{ height: "80px", width: "auto", objectFit: "contain" }} />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/" testid="nav-home">{t("nav.home")}</NavLink>
          <NavLink to="/galeria" testid="nav-gallery">{t("nav.gallery")}</NavLink>
          <NavLink to="/cerca" testid="nav-nearby">{t("nav.nearby")}</NavLink>
          {isAuth && (
            <Link
              to="/comunidad"
              data-testid="nav-community"
              className="relative text-sm uppercase tracking-[0.2em] transition-colors"
              style={{
                color: loc.pathname === "/comunidad" ? "var(--brand-primary)" : "var(--text-secondary)",
              }}
              onClick={() => setOpen(false)}
            >
              {t("nav.community")}
              {unread > 0 && (
                <span
                  className="absolute -top-2 -right-4 inline-flex items-center justify-center rounded-full text-[10px] font-bold"
                  style={{
                    minWidth: 18, height: 18, padding: "0 5px",
                    background: "var(--brand-primary)", color: "#fff",
                    boxShadow: "0 0 8px rgba(200,16,46,0.6)",
                  }}
                  data-testid="nav-community-badge"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Link>
          )}
          <Link
            to="/vip"
            data-testid="nav-vip nav-planes"
            className="text-sm uppercase tracking-[0.2em] transition-colors"
            style={{
              color: loc.pathname === "/vip" ? "var(--vip-gold)" : "var(--vip-gold)",
              fontWeight: 600,
              letterSpacing: "0.2em",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#F5D76E")}
            onMouseLeave={(e) => (e.target.style.color = "var(--vip-gold)")}
            onClick={() => setOpen(false)}
          >
            {t("nav.vip")}
          </Link>
          <NavLink to="/hoteles" testid="nav-hotels">{t("nav.hotels")}</NavLink>
          {isAuth && <NavLink to="/ajustes" testid="nav-settings">{t("nav.settings")}</NavLink>}
          {isAuth && (user.role === "admin" || user.role === "moderator") && <NavLink to="/admin" testid="nav-admin">{t("nav.admin")}</NavLink>}
          {isAuth && user.role === "admin" && <NavLink to="/admin/analytics" testid="nav-analytics">{t("nav.analytics")}</NavLink>}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LanguageSwitcher compact />
          {!isAuth && user !== null && (
            <>
              <Link to="/login" className="btn-ghost text-xs" data-testid="nav-login">{t("nav.signin")}</Link>
              <Link to="/registro" className="btn-primary text-xs" data-testid="nav-register">{t("nav.signup")}</Link>
            </>
          )}
          {isAuth && (
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="btn-ghost text-xs flex items-center gap-2" data-testid="nav-dashboard">
                <LayoutDashboard size={14} strokeWidth={1.2} /> {t("nav.dashboard")}
              </Link>
              <button onClick={handleLogout} className="btn-ghost text-xs flex items-center gap-2" data-testid="nav-logout">
                <LogOut size={14} strokeWidth={1.2} /> {t("nav.signout")}
              </button>
            </div>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
          {open ? <X color="var(--brand-primary)" /> : <Menu color="var(--brand-primary)" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden px-6 pb-6 flex flex-col gap-5" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid="mobile-menu">
          <div className="pt-5 flex flex-col gap-4">
            <NavLink to="/" testid="m-nav-home">Inicio</NavLink>
            <NavLink to="/galeria" testid="m-nav-gallery">Galería</NavLink>
            <NavLink to="/vip" testid="m-nav-vip">VIP</NavLink>
            {isAuth && user.role === "admin" && <NavLink to="/admin" testid="m-nav-admin">Admin</NavLink>}
            {!isAuth && (
              <div className="flex flex-col gap-3 pt-3">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary w-full" data-testid="m-nav-login">Acceder</Link>
                <Link to="/registro" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="m-nav-register">Unirse</Link>
              </div>
            )}
            {isAuth && (
              <div className="flex flex-col gap-3 pt-3">
                <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-secondary w-full" data-testid="m-nav-dashboard">Panel</Link>
                <button onClick={handleLogout} className="btn-ghost" data-testid="m-nav-logout">Cerrar sesión</button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
